import React, { useEffect, useState, useMemo, useRef } from "react";
// 2026-05-12 — AirDatepicker swapped for a local CustomCalendar (see
// below). Imports preserved as comments so revert is a one-line swap:
// uncomment the three lines below + the `openCalendar` function body
// further down + change the button's onClick back to `openCalendar`.
// import AirDatepicker from "air-datepicker";
// import "air-datepicker/air-datepicker.css";
// import localeEn from "air-datepicker/locale/en";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BdrSelectPopup from "../../../../components/BdrInput/BdrSelectPopup";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import "./eventDateTimeInput.css";

// ─────────────────────────────────────────────────────────────────────
// CustomCalendar — local dark-themed date picker that replaces
// AirDatepicker. Matches the modal gradient (#1565c0 → #7b1fa2) so it
// reads as part of the same surface, with frosty white hover on day
// cells (same idiom as the time-entry rows on the Payroll Report).
//
// Behavior parity with the prior AirDatepicker config:
//   - Click a day to highlight it; click "Select" to commit.
//   - Click "Today" to commit today's date immediately.
//   - Click backdrop or press Escape to cancel.
//   - Month + year nav (‹‹ ‹ Month Year › ››).
// ─────────────────────────────────────────────────────────────────────
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

const sameDay = (a, b) =>
  a && b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

// ── Pay-period helpers (payPeriodMode, 2026-07-30) ──
// Thu→Wed pay period, matching PayrollReportPage / server payCalc. All day
// arithmetic is (year, month, day + offset) construction — never
// setDate/±Duration — per the DST-safety rule. Period distance is computed
// on UTC day numbers so a DST hour can't skew the divide-by-7.
const PAY_PERIOD_START_DOW = 4; // Thursday
const periodStartFor = (d) => {
  const back = (d.getDay() - PAY_PERIOD_START_DOW + 7) % 7;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - back);
};
const periodEndFor = (d) => {
  const s = periodStartFor(d);
  return new Date(s.getFullYear(), s.getMonth(), s.getDate() + 6); // Wednesday
};
const utcDayNum = (d) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 864e5;

// Must match the total exit time in eventDateTimeInput.css.
// Current stagger:
//   - Panel exit:    0ms delay,   200ms duration → done at 200ms
//   - Backdrop exit: 150ms delay, 200ms duration → done at 350ms ← total
// CAL_ANIM_MS is the delay before the parent's onSelect/onCancel fires
// so it must equal the LONGEST exit (backdrop's end = 350ms). If you
// tweak the CSS delays/durations, update this constant too.
const CAL_ANIM_MS = 350;

// Exported (2026-07-30) for reuse as the app-wide standard date popup —
// first external consumer: PaystubModal's Generate Multiple range pickers.
// Keep the export in sync if this component is ever renamed/moved.
//
// payPeriodMode (2026-07-30): the unit of selection becomes a whole Thu→Wed
// pay period — alternating periods zebra-stripe, hovering any day lights up
// its entire period, clicking (or Today) commits the period's WEDNESDAY, and
// the footer shows the hovered/picked period's offset from the current one.
export const CustomCalendar = ({ initialDate, onSelect, onCancel, payPeriodMode = false }) => {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const startDate = initialDate || today;

  const [viewYear, setViewYear] = useState(startDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(startDate.getMonth());
  const [picked, setPicked] = useState(initialDate || null);
  const [closing, setClosing] = useState(false);
  // payPeriodMode only — the day the mouse is over, driving whole-period
  // hover highlight + the footer offset readout.
  const [hoverDay, setHoverDay] = useState(null);

  const currentPeriodStart = useMemo(() => periodStartFor(today), [today]);
  // Whole periods between a day's period and the CURRENT one: 0 = current,
  // −1 = last period, +1 = next. Exact integer by construction (UTC days / 7).
  const periodOffsetFor = (day) =>
    Math.round((utcDayNum(periodStartFor(day)) - utcDayNum(currentPeriodStart)) / 7);

  // Ref guards against re-entry from rapid double-clicks / Escape spam.
  // Once an exit animation is in flight, additional close attempts are
  // ignored until the timeout completes and the parent unmounts us.
  const closingRef = useRef(false);

  const startClose = (callback) => {
    if (closingRef.current) return;
    closingRef.current = true;
    setClosing(true);
    setTimeout(callback, CAL_ANIM_MS);
  };

  useEffect(() => {
    const onKey = (ev) => {
      if (ev.key === "Escape") startClose(onCancel);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // onCancel reference is stable enough in practice (parent uses an
    // inline arrow but the popup lives for ~350ms so re-binding is fine);
    // we deliberately omit it from deps to avoid re-attaching the listener
    // on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDow = new Date(viewYear, viewMonth, 1).getDay();

  const stepMonth = (delta) => {
    let m = viewMonth + delta;
    let y = viewYear;
    while (m < 0) { m += 12; y -= 1; }
    while (m > 11) { m -= 12; y += 1; }
    setViewMonth(m);
    setViewYear(y);
  };

  const handleToday = () =>
    startClose(() => onSelect(payPeriodMode ? periodEndFor(today) : new Date()));
  const handleDayClick = (thisDay) => {
    // One-click commit: pick the day AND fire the close animation in
    // the same gesture. The setPicked is kept so the chosen day flashes
    // the picked-style (white fill) during the exit animation — small
    // but useful "your click registered" feedback before unmount.
    // payPeriodMode: whichever day was clicked, commit its period's
    // WEDNESDAY — the canonical period-end the servers key on.
    const commitDay = payPeriodMode ? periodEndFor(thisDay) : thisDay;
    setPicked(commitDay);
    startClose(() => onSelect(commitDay));
  };
  const handleBackdropClick = () => startClose(onCancel);

  // Fixed 6-row grid (42 cells) so the panel height stays constant
  // across 4-/5-/6-week months. Without this, the action buttons jump
  // when the month changes and users miss-click them.
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length < 42) cells.push(null);

  return (
    <div
      className={`evt-dt-cal-backdrop${closing ? " is-closing" : ""}`}
      onClick={handleBackdropClick}
    >
      <div className="evt-dt-cal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="evt-dt-cal-nav">
          <button
            type="button"
            className="evt-dt-cal-nav-btn"
            onClick={() => setViewYear(viewYear - 1)}
            aria-label="Previous year"
          >
            «
          </button>
          <button
            type="button"
            className="evt-dt-cal-nav-btn"
            onClick={() => stepMonth(-1)}
            aria-label="Previous month"
          >
            ‹
          </button>
          <span className="evt-dt-cal-title">
            {MONTH_NAMES[viewMonth]} {viewYear}
          </span>
          <button
            type="button"
            className="evt-dt-cal-nav-btn"
            onClick={() => stepMonth(1)}
            aria-label="Next month"
          >
            ›
          </button>
          <button
            type="button"
            className="evt-dt-cal-nav-btn"
            onClick={() => setViewYear(viewYear + 1)}
            aria-label="Next year"
          >
            »
          </button>
        </div>

        <div className="evt-dt-cal-dow">
          {DAY_LABELS.map((d, i) => (
            <span key={i} className="evt-dt-cal-dow-cell">
              {d}
            </span>
          ))}
        </div>

        <div
          className={`evt-dt-cal-grid${payPeriodMode ? " pp-mode" : ""}`}
          onMouseLeave={payPeriodMode ? () => setHoverDay(null) : undefined}
        >
          {cells.map((d, i) => {
            if (d === null) {
              return (
                <span
                  key={i}
                  className="evt-dt-cal-cell-blank"
                  onMouseEnter={payPeriodMode ? () => setHoverDay(null) : undefined}
                />
              );
            }
            const thisDay = new Date(viewYear, viewMonth, d);
            const isToday = sameDay(thisDay, today);
            const isPicked = sameDay(thisDay, picked);
            let ppCls = "";
            if (payPeriodMode) {
              const pStart = periodStartFor(thisDay);
              const off = periodOffsetFor(thisDay);
              ppCls += ((off % 2) + 2) % 2 === 0 ? " pp-even" : " pp-odd";
              if (hoverDay && sameDay(pStart, periodStartFor(hoverDay))) ppCls += " pp-hover";
              if (picked && sameDay(pStart, periodStartFor(picked))) ppCls += " pp-picked";
            }
            return (
              <button
                type="button"
                key={i}
                className={`evt-dt-cal-cell${isToday ? " is-today" : ""}${
                  isPicked ? " is-picked" : ""
                }${ppCls}`}
                onClick={() => handleDayClick(thisDay)}
                onMouseEnter={payPeriodMode ? () => setHoverDay(thisDay) : undefined}
              >
                {d}
              </button>
            );
          })}
        </div>

        {/* 2026-05-12 — Select button removed; clicking a day commits
            directly. Today button remains as a one-click shortcut to
            commit today's date from any month view (in payPeriodMode it
            commits the CURRENT period's Wednesday). */}
        <div className={`evt-dt-cal-actions${payPeriodMode ? " pp-actions" : ""}`}>
          {payPeriodMode && (
            <span className="evt-dt-cal-pp-status">
              {(() => {
                const target = hoverDay || picked;
                if (!target) return <span className="evt-dt-cal-pp-hint">Pick a pay period</span>;
                const off = periodOffsetFor(target);
                const s = periodStartFor(target);
                const e = periodEndFor(target);
                const fmt = (dd) => dd.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                const tag = off === 0 ? "Current" : off > 0 ? `+${off}` : `−${Math.abs(off)}`;
                return (
                  <>
                    <b>{tag}</b> {fmt(s)} – {fmt(e)}
                  </>
                );
              })()}
            </span>
          )}
          <button
            type="button"
            className="evt-dt-cal-action-btn evt-dt-cal-action-today"
            onClick={handleToday}
          >
            Today
          </button>
        </div>
      </div>
    </div>
  );
};

// Default hour range (6 AM through 10 PM) — covers normal job-day windows.
// Time-entry editing callers pass hourRange={[0, 23]} for full-day coverage.
// Records with off-hours times (legacy data, vacation events, etc.) still
// render correctly: buildHourOptions adds any out-of-range value to the
// dropdown so users can see and adjust it.
const DEFAULT_HOUR_RANGE = [6, 22];

const hourLabel = (h) => {
  if (h === 0) return "12 AM";
  if (h === 12) return "12 PM";
  if (h > 12) return `${h - 12} PM`;
  return `${h} AM`;
};

const buildHourOptions = (currentHour24, hourRange) => {
  const [lo, hi] = hourRange || DEFAULT_HOUR_RANGE;
  const inRange = [];
  for (let h = lo; h <= hi; h++) inRange.push(h);
  if (inRange.includes(currentHour24)) return inRange;
  return [...inRange, currentHour24].sort((a, b) => a - b);
};

const buildMinuteOptions = (currentMinute, minuteStep) => {
  const step = minuteStep || 15;
  const opts = [];
  for (let m = 0; m < 60; m += step) opts.push(m);
  if (!opts.includes(currentMinute)) {
    opts.push(currentMinute);
    opts.sort((a, b) => a - b);
  }
  return opts;
};

// Snap a raw minute value to the nearest step. Used when minuteStep > 1
// (e.g. calendar events round to :00/:15/:30/:45). Bypassed entirely
// when minuteStep === 1 (time-entry editing — admins must be able to
// record exact minutes without silent rounding).
const snapMinute = (rawMinute, minuteStep) => {
  const step = minuteStep || 15;
  if (step === 1) return rawMinute;
  const snapped = Math.round(rawMinute / step) * step;
  return snapped >= 60 ? 60 - step : snapped;
};

// Convert a defaultTime prop to a 24-hour integer.
// Accepts both new shape ({hour24, minute}) and legacy shape
// ({hour: 1-12, minute, amPm: "AM"|"PM"}) so existing callers keep working.
const defaultToHour24 = (defaultTime) => {
  if (!defaultTime) return 8;
  if (typeof defaultTime.hour24 === "number") return defaultTime.hour24;
  if (defaultTime.amPm) {
    const h = defaultTime.hour ?? 8;
    if (h === 12) return defaultTime.amPm === "AM" ? 0 : 12;
    return defaultTime.amPm === "PM" ? h + 12 : h;
  }
  return defaultTime.hour ?? 8;
};

// Build epoch-ms string from a Date + 24-hour time parts.
const merge = (date, hour24, minute) => {
  const merged = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hour24,
    minute,
    0
  );
  return merged.getTime().toString();
};

// Decompose epoch ms string into { selectedDate, hour24, minute }.
// When minuteStep === 1, the raw minute is preserved (no rounding).
const decompose = (valueStr, minuteStep) => {
  if (!valueStr) {
    return { selectedDate: null, hour24: 8, minute: 0 };
  }

  const ms = parseInt(valueStr, 10);
  if (isNaN(ms)) {
    return { selectedDate: null, hour24: 8, minute: 0 };
  }

  const d = new Date(ms);
  const hour24 = d.getHours();
  const minute = snapMinute(d.getMinutes(), minuteStep);

  return { selectedDate: d, hour24, minute };
};

const EventDateTimeInput = ({
  value,
  onChange,
  label,
  hideTime,
  defaultTime,
  // Added 2026-05-12 for time-entry editing reuse.
  // Defaults preserve original calendar-event behavior.
  minuteStep = 15,
  hourRange,
  lockedDate = null,
}) => {
  // Generate a stable unique ID for the AirDatepicker anchor element.
  const anchorId = useMemo(
    () => `evt-dt-input-${Math.random().toString(36).slice(2, 8)}`,
    []
  );

  const [selectedDate, setSelectedDate] = useState(null);
  const [hour24, setHour24] = useState(defaultToHour24(defaultTime));
  const [minute, setMinute] = useState(defaultTime?.minute ?? 0);
  // 2026-05-12 — replaces AirDatepicker's imperative show/hide pattern.
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Keep a ref to the latest time state. (No longer strictly needed
  // after the AirDatepicker swap — React state in handleCalendarSelect
  // is always fresh — but preserved so the commented-out `openCalendar`
  // below works if someone reverts to AirDatepicker.)
  const timeRef = useRef({ hour24, minute });
  useEffect(() => {
    timeRef.current = { hour24, minute };
  }, [hour24, minute]);

  // React to external value changes (e.g. invoice prefill).
  // When lockedDate is non-null, the displayed date is forced to it
  // regardless of what the value epoch decomposes to — guarantees the
  // payroll-page edit modal keeps both clock-in and clock-out pinned to
  // the entry's date even if the parent's two value props momentarily
  // disagree on the date portion during state init.
  useEffect(() => {
    const parsed = decompose(value, minuteStep);
    setSelectedDate(lockedDate || parsed.selectedDate);
    if (!parsed.selectedDate && defaultTime) {
      setHour24(defaultToHour24(defaultTime));
      setMinute(defaultTime.minute ?? 0);
    } else {
      setHour24(parsed.hour24);
      setMinute(parsed.minute);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, lockedDate]);

  // 2026-05-12 — replaced by handleCalendarSelect + the <CustomCalendar>
  // render. Preserved as a comment for one-step revert. To restore:
  //   1. Uncomment the AirDatepicker imports at the top of this file.
  //   2. Uncomment this function body.
  //   3. Change the button's onClick back to `openCalendar`.
  //   4. Remove the <CustomCalendar /> render block.
  //
  // const openCalendar = () => {
  //   const buttonArr = [
  //     {
  //       content: "Select",
  //       className: "evt-dt-cal-start-button bdr-border-thin bdr-shadow-subtle",
  //       onClick: () => {
  //         const pickedDate = cal.lastSelectedDate || new Date();
  //         const { hour24: h, minute: m } = timeRef.current;
  //         setSelectedDate(pickedDate);
  //         const epochStr = merge(pickedDate, h, m);
  //         if (onChange) onChange(epochStr);
  //         cal.hide();
  //         return setTimeout(() => cal.destroy(), 500);
  //       },
  //     },
  //     {
  //       content: "Today",
  //       className: "evt-dt-cal-start-button bdr-border-thin bdr-shadow-subtle",
  //       onClick: () => {
  //         const today = new Date();
  //         const { hour24: h, minute: m } = timeRef.current;
  //         setSelectedDate(today);
  //         const epochStr = merge(today, h, m);
  //         if (onChange) onChange(epochStr);
  //         cal.hide();
  //         return setTimeout(() => cal.destroy(), 500);
  //       },
  //     },
  //   ];
  //   const cal = new AirDatepicker(`#${anchorId}`, {
  //     isMobile: true,
  //     timepicker: false,
  //     autoClose: false,
  //     locale: localeEn,
  //     buttons: buttonArr,
  //     onHide: () => {
  //       setTimeout(() => {
  //         try { cal.destroy(); } catch (_) {}
  //       }, 500);
  //     },
  //   });
  //   const focusDate = selectedDate || new Date();
  //   cal.setFocusDate(focusDate, { viewDateTransition: true });
  //   if (selectedDate) cal.selectDate(focusDate, { silent: true });
  //   cal.show();
  //   const docWidth = window.innerWidth / 2;
  //   const docHeight = window.innerHeight / 2;
  //   const dpHeight = cal.$datepicker.clientHeight / 2;
  //   cal.$datepicker.style.top = `${docHeight - dpHeight}px`;
  //   cal.$datepicker.style.left = `${docWidth}px`;
  //   cal.$datepicker.style.transform = "translateX(-50%)";
  //   cal.$datepicker.style.zIndex = "10000";
  // };

  // Commit handler called by CustomCalendar's Today and Select buttons.
  // Mirrors the prior AirDatepicker callbacks: set local date state,
  // merge with current hour/minute, fire onChange, close the popup.
  const handleCalendarSelect = (pickedDate) => {
    setSelectedDate(pickedDate);
    const epochStr = merge(pickedDate, hour24, minute);
    if (onChange) onChange(epochStr);
    setCalendarOpen(false);
  };

  const handleTimeChange = (newHour24, newMinute, dateOverride) => {
    const dateToUse = dateOverride !== undefined ? dateOverride : selectedDate;
    if (dateToUse) {
      const epochStr = merge(dateToUse, newHour24, newMinute);
      if (onChange) onChange(epochStr);
    }
  };

  const handleHourChange = (e) => {
    const newHour24 = parseInt(e.target.value, 10);
    setHour24(newHour24);
    handleTimeChange(newHour24, minute);
  };

  const handleMinuteChange = (e) => {
    const newMinute = parseInt(e.target.value, 10);
    setMinute(newMinute);
    handleTimeChange(hour24, newMinute);
  };

  const dateDisplayText = selectedDate
    ? BAGroupTimeFormat(selectedDate.getTime().toString(), false, false)
    : null;

  const hourOptions = buildHourOptions(hour24, hourRange);
  const minuteOptions = buildMinuteOptions(minute, minuteStep);

  return (
    <div className="evt-dt-container">
      <input type="hidden" id={anchorId} />

      {label && <span className="evt-dt-label">{label}</span>}

      {/*
        Icon AND date text are ONE button. They used to be siblings — a 1.2rem
        icon was the entire hit target, and the date beside it looked
        clickable but wasn't, which is a small target and a confusing one.
        Wrapping both also lets flex centre the icon against the text instead
        of the two aligning to the row.

        A locked date has no picker, so it stays a plain span; a button that
        does nothing is worse than no button.
      */}
      {lockedDate ? (
        <span className="evt-dt-date-display evt-dt-date-locked">
          {dateDisplayText}
        </span>
      ) : (
        <button
          type="button"
          className="evt-dt-date-trigger"
          onClick={() => setCalendarOpen(true)}
          aria-label={
            dateDisplayText ? `Change date, ${dateDisplayText}` : "Select date"
          }
        >
          <CalendarMonthIcon sx={{ fontSize: "1.2rem" }} />
          <span
            className={`evt-dt-date-display${
              dateDisplayText ? "" : " evt-dt-date-placeholder"
            }`}
          >
            {dateDisplayText || "Select Date"}
          </span>
        </button>
      )}

      {!hideTime && (
        <div className="evt-dt-time-group">
          {/*
            The hour list is long enough to need a height cap, which a native
            <select> cannot have — its popup is OS chrome. It was a MUI
            `Select` for that reason, but MUI portals its menu to <body> while
            leaving it in the REACT tree here, so every mousedown in the open
            menu — an option OR the invisible backdrop — bubbled to
            BdrModal's overlay, which saw a target outside its panel and closed
            the whole event modal. BdrSelectPopup is the capped list that
            handles exactly that; see note 2 in its header.
          */}
          <BdrSelectPopup
            id={`${anchorId}-hour`}
            value={hour24}
            onChange={handleHourChange}
            options={hourOptions.map((h) => ({
              value: h,
              label: hourLabel(h),
            }))}
            className="evt-dt-select-trigger bdr-select-trigger"
            /* the trigger is ~55px — without this every "9 PM" wraps */
            fitContent
          />

          <span className="evt-dt-colon">:</span>

          <BdrSelectPopup
            id={`${anchorId}-minute`}
            className="evt-dt-select-trigger bdr-select-trigger"
            value={minute}
            onChange={handleMinuteChange}
            fitContent
            options={minuteOptions.map((m) => ({
              value: m,
              label: String(m).padStart(2, "0"),
            }))}
          />
        </div>
      )}

      {calendarOpen && (
        <CustomCalendar
          initialDate={selectedDate}
          onSelect={handleCalendarSelect}
          onCancel={() => setCalendarOpen(false)}
        />
      )}
    </div>
  );
};

export default EventDateTimeInput;
